import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import styles from './Workflows2.module.css';
import { FlowTimeline } from '@/components/engineering/FlowTimeline';
import { usePlatformAccess } from '@/services/platform';
import { useSites } from '@/hooks/useConfig';
import {
  useArtigos,
  useVerticais,
  useResultado,
  useEngenhariaRunDetalhe,
  useRerunRun,
  usePublicarRun,
} from '@/services/workflows2';
import type { WorkflowRunStageOut } from '@/services/workflows2';
import { PainelDeErro, Aguardando, ehEndpointAusente } from '@/components/engineering';
import { PayloadPanel } from '@/components/workflows2/PayloadPanel';
import { EditorJson } from '@/components/workflows2/EditorJson';
import { MoldeFrame } from '@/components/workflows2/MoldeFrame';
import { EstruturaViva } from '@/components/workflows2/EstruturaViva';

export function Workflows2() {
  const [searchParams, setSearchParams] = useSearchParams();
  const client = searchParams.get('client') || '';
  const site = searchParams.get('site') || '';
  const vertical = searchParams.get('vertical') || '';
  const runId = searchParams.get('run') || '';

  const { isStaff, clients } = usePlatformAccess();
  const { data: sites = [], isLoading: isLoadingSites } = useSites();

  // Staff enxerga todas as marcas: sem ?site= o backend recusa (400), porque
  // não há como saber de quem são as verticais pedidas. Tenant não escolhe
  // marca (o token já é da marca dele), então a consulta sempre dispara.
  const semMarcaObrigatoria = isStaff && !site;

  const {
    data: verticais = [],
    isLoading: isLoadingVerticais,
    error: errorVerticais,
  } = useVerticais(site || null, { enabled: !semMarcaObrigatoria });
  const {
    data: artigos = [],
    isLoading: isLoadingArtigos,
    error: errorArtigos,
  } = useArtigos(site, vertical);
  const {
    data: runData,
    isLoading: isLoadingRun,
    error: errorRun,
  } = useEngenhariaRunDetalhe(runId);
  const {
    data: resultado,
    isLoading: isLoadingResultado,
    error: errorResultado,
  } = useResultado(site, vertical);

  const { mutateAsync: rerunRun } = useRerunRun();
  const { mutateAsync: publicarRun } = usePublicarRun();

  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<
    Record<string, { system?: string; params?: Record<string, unknown> }>
  >({});

  const handleClientChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSearchParams((p) => {
      p.set('client', e.target.value);
      return p;
    });
  };
  const handleSiteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSearchParams((p) => {
      p.set('site', e.target.value);
      return p;
    });
  };
  const handleVerticalChange = (id: string) => {
    setSearchParams((p) => {
      p.set('vertical', id);
      return p;
    });
  };

  const handleRerun = async () => {
    if (!runId || !selectedStageId) return;
    try {
      await rerunRun({
        id: runId,
        payload: {
          from_stage: selectedStageId,
          overrides,
        },
      });
      alert('Rerun iniciado com sucesso.');
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { detail?: string } } };
      if (err.response?.status === 409) {
        alert(err.response?.data?.detail || 'Conflito (409)');
      } else {
        alert('Erro ao re-rodar: ' + (e instanceof Error ? e.message : String(e)));
      }
    }
  };

  const handlePublish = async () => {
    if (!runId) return;
    const confirm1 = window.confirm('Você tem certeza que deseja publicar?');
    if (!confirm1) return;
    const confirm2 = window.confirm('Confirmação final: publicar?');
    if (!confirm2) return;

    try {
      await publicarRun({
        id: runId,
        payload: { status: 'publish' },
      });
      alert('Publicação solicitada com sucesso.');
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { detail?: string } } };
      if (err.response?.status === 409) {
        alert(err.response?.data?.detail || 'Conflito (409)');
      } else {
        alert('Erro ao publicar: ' + (e instanceof Error ? e.message : String(e)));
      }
    }
  };

  const activeStage =
    runData?.stages?.find((s) => s.stage_id === selectedStageId) ||
    (runData?.stages?.[0] as WorkflowRunStageOut | undefined);

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1>Workflows 2.0</h1>
        <div className={styles.filters} style={{ display: 'flex', gap: '16px', margin: '16px 0' }}>
          {isStaff && (
            <select value={client} onChange={handleClientChange} title="Client">
              <option value="">-- Selecione o Client --</option>
              {clients?.map((c: { id: string; name: string }) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.id}
                </option>
              ))}
            </select>
          )}

          {isLoadingSites ? (
            <span>Carregando sites...</span>
          ) : (
            <select value={site} onChange={handleSiteChange} title="Site">
              <option value="">-- Selecione o Site --</option>
              {sites.map((s: { id?: string; slug?: string; name?: string }) => (
                <option key={s.id || s.slug} value={s.slug || s.id}>
                  {s.name || s.slug}
                </option>
              ))}
            </select>
          )}
        </div>
      </header>

      {semMarcaObrigatoria ? (
        <p>Selecione a marca para ver as verticais.</p>
      ) : (
        <>
          {errorVerticais && !ehEndpointAusente(errorVerticais) && (
            <PainelDeErro
              endpoint="GET /workflows2/verticais"
              o_que="as verticais"
              erro={errorVerticais}
            />
          )}

          {isLoadingVerticais ? (
            <Aguardando o_que="verticais" endpoint="GET /workflows2/verticais" />
          ) : (
            <nav
              className={styles.tabs}
              style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}
            >
              {verticais.map((v) => (
                <button
                  key={v.id}
                  className={vertical === v.id ? styles.activeTab : ''}
                  onClick={() => handleVerticalChange(v.id)}
                  style={{
                    opacity: v.habilitada ? 1 : 0.5,
                    border:
                      vertical === v.id ? '2px solid var(--text-1)' : '1px solid var(--border-2)',
                    padding: '8px 16px',
                    background: vertical === v.id ? 'var(--bg-2)' : 'var(--bg-1)',
                    color: 'var(--text-1)',
                  }}
                  title={v.motivo || ''}
                >
                  {v.nome} {!v.habilitada && '(Desabilitada)'}
                </button>
              ))}
            </nav>
          )}
        </>
      )}

      {(() => {
        const selVert = verticais.find((v) => v.id === vertical);
        if (!selVert) return null;
        return (
          <div
            style={{
              marginBottom: '24px',
              padding: '16px',
              border: '1px solid var(--border-2)',
              borderRadius: '8px',
              background: 'var(--bg-1)',
            }}
          >
            <h3 style={{ margin: '0 0 12px 0', color: 'var(--text-1)' }}>Detalhes da Vertical</h3>

            {!selVert.habilitada && (
              <p
                style={{
                  color: 'var(--sig-crit-text)',
                  background: 'var(--sig-crit-bg)',
                  padding: '8px',
                  border: '1px solid var(--sig-crit-border)',
                  borderRadius: '4px',
                  marginBottom: '12px',
                }}
              >
                <strong>Habilitada: Falso</strong> — {selVert.motivo}
              </p>
            )}

            {selVert.prontidao && selVert.prontidao.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <strong style={{ color: 'var(--text-1)' }}>Prontidão:</strong>
                <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0' }}>
                  {selVert.prontidao.map((p, idx) => (
                    <li
                      key={idx}
                      style={{
                        color: p.ok ? 'var(--text-2)' : 'var(--sig-crit-text)',
                        marginBottom: '4px',
                      }}
                    >
                      {p.ok ? '✅' : '❌'} {p.requisito}: {p.detalhe}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div style={{ marginBottom: '16px', color: 'var(--text-2)' }}>
              <strong>Retenção (dias):</strong> {selVert.retencao_dias}
            </div>

            <div>
              <button
                disabled={!selVert.editoria_fabrica_slug}
                title={
                  !selVert.editoria_fabrica_slug
                    ? 'Nenhuma editoria fábrica vinculada'
                    : 'Rodar agora'
                }
                style={{
                  background: selVert.editoria_fabrica_slug ? 'var(--nexus-brand)' : 'var(--bg-3)',
                  color: selVert.editoria_fabrica_slug ? 'var(--bg-0)' : 'var(--text-3)',
                  padding: '8px 16px',
                  border: 'none',
                  cursor: selVert.editoria_fabrica_slug ? 'pointer' : 'not-allowed',
                  borderRadius: '4px',
                }}
              >
                Rodar agora
              </button>
            </div>
          </div>
        );
      })()}

      <div className={styles.layout} style={{ display: 'flex', gap: '24px' }}>
        <div className={styles.leftPanel} style={{ flex: 1, minWidth: '0' }}>
          <section className={styles.section}>
            <h2>Artigos</h2>
            {isLoadingArtigos && <Aguardando o_que="artigos" endpoint="GET /workflows2/artigos" />}
            {errorArtigos && !ehEndpointAusente(errorArtigos) && (
              <PainelDeErro
                endpoint="GET /workflows2/artigos"
                o_que="os artigos"
                erro={errorArtigos}
              />
            )}
            {!isLoadingArtigos && !errorArtigos && (
              <ul
                style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid var(--border-2)',
                  padding: '8px',
                  color: 'var(--text-1)',
                  listStyle: 'none',
                  margin: 0,
                }}
              >
                {artigos.map((a) => (
                  <li
                    key={a.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '4px',
                    }}
                  >
                    <span>{a.titulo}</span>
                    <span
                      style={{
                        fontSize: '12px',
                        background: 'var(--bg-2)',
                        color: 'var(--text-2)',
                        padding: '2px 4px',
                        borderRadius: '4px',
                      }}
                    >
                      {a.status}
                    </span>
                  </li>
                ))}
                {artigos.length === 0 && <li>Nenhum artigo encontrado.</li>}
              </ul>
            )}
          </section>

          {runId && (
            <section className={styles.section} style={{ marginTop: '24px' }}>
              <h2>Run Detalhe</h2>
              {isLoadingRun && (
                <Aguardando o_que="Run" endpoint="GET /workflows2/engenharia/runs/:id" />
              )}
              {errorRun && (
                <PainelDeErro
                  endpoint={`GET /workflows2/engenharia/runs/${runId}`}
                  o_que="os detalhes da run"
                  erro={errorRun}
                />
              )}

              {runData && (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <FlowTimeline
                      stages={(runData.stages || []).map((s, i) => ({
                        id: s.stage_id,
                        nome: `Stage ${s.seq || i}`,
                        ordem: s.seq || i,
                        kind: s.system_enviado ? 'ia' : 'codigo',
                        model: s.model_pedido || null,
                        role_system: null,
                        default_system: null,
                        is_overridden: false,
                        user_template: null,
                        saida: s.saida_texto || null,
                      }))}
                      selectedId={selectedStageId || activeStage?.stage_id || null}
                      onSelect={setSelectedStageId}
                      outputs={{}}
                    />
                  </div>

                  {activeStage && (
                    <div style={{ marginTop: '16px' }}>
                      <PayloadPanel stage={activeStage} isStaff={isStaff} />
                    </div>
                  )}

                  {isStaff && (
                    <div
                      style={{
                        marginTop: '24px',
                        border: '1px solid var(--border-2)',
                        padding: '16px',
                        background: 'var(--bg-1)',
                      }}
                    >
                      <h3>Overrides (Staff)</h3>
                      <EditorJson
                        initialValue={overrides}
                        onChange={(val) =>
                          setOverrides(
                            (val as Record<
                              string,
                              { system?: string; params?: Record<string, unknown> }
                            >) || {},
                          )
                        }
                        variables={['system', 'params']}
                      />
                      <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
                        <button
                          onClick={handleRerun}
                          style={{
                            background: 'var(--nexus-brand)',
                            color: 'var(--bg-0)',
                            padding: '8px 16px',
                            border: 'none',
                            cursor: 'pointer',
                            borderRadius: '4px',
                          }}
                        >
                          Re-rodar daqui
                        </button>
                        <button
                          onClick={handlePublish}
                          style={{
                            background: 'var(--sig-ok)',
                            color: 'var(--bg-0)',
                            padding: '8px 16px',
                            border: 'none',
                            cursor: 'pointer',
                            borderRadius: '4px',
                          }}
                        >
                          Publicar
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
          )}
        </div>

        <div className={styles.rightPanel} style={{ flex: 1, minWidth: '0' }}>
          <section className={styles.section}>
            <h2>MoldeFrame</h2>
            {site && <MoldeFrame siteSlug={site} />}
            {!site && <p>Selecione um site para ver o molde.</p>}
          </section>

          <section className={styles.section} style={{ marginTop: '24px' }}>
            <h2>Estrutura Viva</h2>
            {activeStage?.saida_estruturada ? (
              <EstruturaViva data={activeStage.saida_estruturada} />
            ) : (
              <p>Nenhuma saída estruturada selecionada.</p>
            )}
          </section>

          <section className={styles.section} style={{ marginTop: '24px' }}>
            <h2>Resultado (Analytics)</h2>
            {isLoadingResultado && (
              <Aguardando o_que="resultado" endpoint="GET /workflows2/resultado" />
            )}
            {errorResultado && !ehEndpointAusente(errorResultado) && (
              <PainelDeErro
                endpoint="GET /workflows2/resultado"
                o_que="os resultados"
                erro={errorResultado}
              />
            )}
            {resultado &&
              (resultado.configurado ? (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '14px',
                      color: 'var(--text-1)',
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            borderBottom: '1px solid var(--border-2)',
                            textAlign: 'left',
                            padding: '4px',
                          }}
                        >
                          Título
                        </th>
                        <th
                          style={{
                            borderBottom: '1px solid var(--border-2)',
                            textAlign: 'right',
                            padding: '4px',
                          }}
                        >
                          Pageviews
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(resultado.linhas || []).map((l, i) => (
                        <tr key={i}>
                          <td style={{ borderBottom: '1px solid var(--border-1)', padding: '4px' }}>
                            {l.titulo}
                          </td>
                          <td
                            style={{
                              borderBottom: '1px solid var(--border-1)',
                              padding: '4px',
                              textAlign: 'right',
                            }}
                          >
                            {l.pageviews}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p
                  style={{
                    background: 'var(--sig-warn-bg)',
                    color: 'var(--sig-warn-text)',
                    padding: '12px',
                    border: '1px solid var(--sig-warn-border)',
                  }}
                >
                  Configurado: Falso. {resultado.motivo}.{' '}
                  <Link to="/config/analytics" style={{ fontWeight: 'bold' }}>
                    Configurar Analytics
                  </Link>
                </p>
              ))}
          </section>
        </div>
      </div>
    </div>
  );
}
